import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client/react'

import { GET_PRODUCTS } from '../Queries'
import { formatPrice } from '../../hooks/useCart'
import { IS_TOUCH } from '../../config/touch'
import {
  searchOpenStore,
  watchOpenStore,
  selectedProductStore,
  cartOpenStore,
  lightboxStore,
  getProductObject,
  relockPointer,
  useStore,
} from '../../store/uiStore'

// ============================================================
// Ricerca prodotti, stile command-palette: app "Cerca" dell'orologio →
// pannello glass con input e risultati live (miniatura, nome,
// prezzo). Scegliendo un risultato il prodotto vola in mano
// (selectedProductStore → vetrina, nuvoletta, carosello).
// I tasti digitati NON arrivano al gioco (stopPropagation sul
// keydown dell'input: niente WASD/E/C mentre scrivi).
// ============================================================

const MAX_RISULTATI = 8

// Minuscole e SENZA ACCENTI: "lámpada", "caffe" e "caffè" devono trovare la
// stessa cosa. Prima "lámpada" non trovava la lampada (provato).
const piano = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

// Il prezzo della riga: se le varianti costano diverso, "da" il minimo —
// mostrare solo la prima variante poteva dare un prezzo che non esiste.
function prezzo(p) {
  const prezzi = p.variants.edges.map((e) => e.node.price).filter(Boolean)
  if (prezzi.length === 0) return ''
  const minimo = prezzi.reduce((a, b) => (Number(b.amount) < Number(a.amount) ? b : a))
  const tutti = prezzi.every((x) => x.amount === minimo.amount)
  return (tutti ? '' : 'da ') + formatPrice(minimo)
}

export default function SearchOverlay() {
  const open = useStore(searchOpenStore)
  const [testo, setTesto] = useState('')

  // Stessa query del negozio: risolta dalla cache Apollo, zero rete
  const productsQuery = useQuery(GET_PRODUCTS)
  const prodotti = useMemo(() => {
    if (!productsQuery.data) return []
    return productsQuery.data.products.edges.map(({ node }) => ({
      ...node,
      image: node.images.edges[0]?.node.url || '',
    }))
  }, [productsQuery.data])

  const risultati = useMemo(() => {
    const q = piano(testo.trim())
    if (!q) return prodotti.slice(0, MAX_RISULTATI)
    return prodotti
      .filter((p) => piano(p.title).includes(q) || piano(p.description).includes(q))
      .slice(0, MAX_RISULTATI)
  }, [prodotti, testo])

  // LA RIGA SCELTA si muove con ↑↓ (e col mouse): Invio apre quella, non
  // per forza la prima. Torna in cima a ogni lettera digitata.
  const [scelta, setScelta] = useState(0)
  useEffect(() => { setScelta(0) }, [testo, open])
  const lista = useRef(null)
  useEffect(() => {
    lista.current?.children[scelta]?.scrollIntoView({ block: 'nearest' })
  }, [scelta])

  // Si apre dall'icona sul quadrante dell'orologio, che vive dentro il
  // Canvas e può solo scrivere sullo store: il campo lo ripuliamo qui.
  useEffect(() => {
    if (open) setTesto('')
  }, [open])

  // A RICERCA FINITA IL POLSO SI ABBASSA (chiesto dall'utente): la ricerca
  // si apre dall'orologio, e restare col braccio alzato dopo aver trovato
  // (o lasciato perdere) costringeva a premere Q un'altra volta. Il ritorno
  // della visuale e il riaggancio del mouse sono gli stessi di Q.
  const chiudi = (conRelock) => {
    searchOpenStore.set(false)
    watchOpenStore.set(false)
    if (conRelock) relockPointer()
  }

  const scegli = (p) => {
    // l'oggetto registrato porta con sé activeDiscount (per il prezzo
    // scontato nella nuvoletta); fallback al prodotto della query
    const obj = getProductObject(p.id)
    cartOpenStore.set(false)
    lightboxStore.set(null)
    selectedProductStore.set(obj?.userData?.product || p)
    chiudi(false) // prodotto in mano = cursore libero
  }

  const onKeyDown = (e) => {
    e.stopPropagation() // i tasti digitati non devono muovere il player
    if (e.key === 'Escape') chiudi(true)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault() // senza, il cursore del campo salta a inizio/fine riga
      if (risultati.length === 0) return
      const passo = e.key === 'ArrowDown' ? 1 : -1
      setScelta((i) => (i + passo + risultati.length) % risultati.length)
    }
    if (e.key === 'Enter' && risultati.length > 0) scegli(risultati[Math.min(scelta, risultati.length - 1)])
  }

  return (
    <>
      {open && (
        <div className="search-backdrop" onClick={() => chiudi(true)}>
          <div className="search-panel" onClick={(e) => e.stopPropagation()}>
            <input
              className="search-input"
              type="text"
              placeholder="Cerca un prodotto…"
              value={testo}
              autoFocus
              onChange={(e) => setTesto(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <div className="search-results" ref={lista}>
              {risultati.map((p, i) => (
                <button
                  key={p.id}
                  className={i === scelta ? 'search-result scelta' : 'search-result'}
                  onClick={() => scegli(p)}
                  onMouseEnter={() => setScelta(i)}
                >
                  {p.image
                    ? <img src={`${p.image}${p.image.includes('?') ? '&' : '?'}width=64`} alt="" />
                    : <span className="search-noimg" />}
                  <span className="search-result-title">{p.title}</span>
                  {!p.variants.edges.some((e) => e.node.availableForSale) && (
                    <span className="search-result-esaurito">esaurito</span>
                  )}
                  <span className="search-result-price">{prezzo(p)}</span>
                </button>
              ))}
              {/* mentre il catalogo arriva non si dice "nessun prodotto": non e' vero */}
              {risultati.length === 0 && (
                <div className="search-empty">
                  {productsQuery.error
                    ? 'Il catalogo non risponde: riprova fra poco.'
                    : prodotti.length === 0
                      ? 'Il catalogo si sta caricando…'
                      : `Nessun prodotto per “${testo}”`}
                </div>
              )}
            </div>
            {/* su telefono i tasti non ci sono: si dice come si chiude */}
            <div className="search-hint">
              {IS_TOUCH
                ? 'tocca fuori dal riquadro per chiudere'
                : <><kbd>↑</kbd><kbd>↓</kbd> scegli · <kbd>Invio</kbd> prendi · <kbd>Esc</kbd> chiudi</>}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
